import { IField } from 'app/services/api5-service/api.interface';

import { getInlineFieldGroupConfig } from 'common/dynamic-form/types/group/group.utils';

import { ISPFieldConfig, ISPFieldType, ISPFormState } from '../../model';
import { getConfigsDistributedInPlacesInLayout } from '../get-configs-distributed-in-layout';

interface SeparatedByCondition {
  configsToWrapp: ISPFieldConfig[];
  unwrappedConfigs: ISPFieldConfig[];
}

/**
 * Separate configs for wrapping into inline group. Some configs shouldn't be in inline group ever, like list config
 *
 * @param configs
 */
function separateConfigsForInlineGroup(
  configs: ISPFieldConfig[],
): SeparatedByCondition {
  return configs.reduce<SeparatedByCondition>(
    (acc, config) => {
      if (
        config.props.doNotWrappIntoInlineGroup ||
        config.type === ISPFieldType.Hidden
      ) {
        acc.unwrappedConfigs.push(config);
      } else {
        acc.configsToWrapp.push(config);
      }
      return acc;
    },
    {
      configsToWrapp: [],
      unwrappedConfigs: [],
    },
  );
}

/**
 * Wrap configs into inline group if needed
 *
 * @param configs
 * @param field
 * @param state
 */
function wrapIntoInlineGroupIfNeeded(
  configs: ISPFieldConfig[],
  field: IField,
  state: ISPFormState,
): ISPFieldConfig[] {
  return !configs.some(config => config.wrapIntoInlineGroupIfNeeded) &&
    configs.length <= 1
    ? configs
    : [getInlineFieldGroupConfig(configs, field, state)];
}

/**
 * Wrap configs into inline group config, if needed
 *
 * @param configs - field configs
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function wrappConfigsIntoInlineGroup(
  configs: ISPFieldConfig[],
  field: IField,
  state: ISPFormState,
): ISPFieldConfig[] {
  const { configsToWrapp, unwrappedConfigs } =
    separateConfigsForInlineGroup(configs);
  if (
    !configsToWrapp.some(config => config.wrapIntoInlineGroupIfNeeded) &&
    configsToWrapp.length <= 1
  ) {
    return configs;
  }

  const configsByPlaces = getConfigsDistributedInPlacesInLayout(configsToWrapp);
  return [
    ...wrapIntoInlineGroupIfNeeded(configsByPlaces.aside, field, state),
    ...wrapIntoInlineGroupIfNeeded(configsByPlaces.content, field, state),
    ...wrapIntoInlineGroupIfNeeded(configsByPlaces.footer, field, state),
    ...wrapIntoInlineGroupIfNeeded(configsByPlaces.header, field, state),
    ...wrapIntoInlineGroupIfNeeded(configsByPlaces.summary, field, state),
    ...configsByPlaces.absolute,
    ...unwrappedConfigs,
  ];
}
